/**
 * Game Analytics Component
 * VOY-133: Post-game performance breakdown
 */

import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { BarChart3, Target, Lightbulb } from 'lucide-react';

interface CategoryPerformance {
  category: string;
  correct: number;
  total: number;
  percentile: number; // vs other players
}

interface GameAnalyticsProps {
  categories: CategoryPerformance[];
  speedPercentile: number;
  recommendation: string;
  bestCategory: string;
  onPractice: () => void;
  onStudyTips: () => void;
}

export function GameAnalytics({
  categories,
  speedPercentile,
  recommendation,
  bestCategory,
  onPractice,
  onStudyTips
}: GameAnalyticsProps) {
  const getPerformanceColor = (percentile: number) => {
    if (percentile >= 75) return 'text-green-600';
    if (percentile >= 50) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getPerformanceIcon = (percentile: number) => {
    if (percentile >= 75) return '🟢';
    if (percentile >= 50) return '🟡';
    return '🔴';
  };

  return (
    <Card className="rounded-3xl bg-white/[0.08] dark:bg-zinc-800/50 backdrop-blur-xl border border-white/30 dark:border-zinc-700/50 shadow-[0_8px_24px_rgba(0,0,0,0.15)] dark:shadow-xl">
      <CardContent className="p-5">
        {/* Header */}
        <div className="flex items-center gap-2 mb-4">
          <BarChart3 className="w-5 h-5 text-[#5B6FED]" />
          <h3 className="text-lg font-bold text-gray-900">Your Game Report</h3>
        </div>

        {/* Speed Performance */}
        <div className="mb-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-semibold text-gray-700">Answer Speed</span>
            <span className={`font-bold ${getPerformanceColor(speedPercentile)}`}>
              {getPerformanceIcon(speedPercentile)} Faster than {speedPercentile}% of pool
            </span>
          </div>
        </div>

        {/* Category Performance */}
        <div className="mb-4">
          <p className="text-sm font-semibold text-gray-700 mb-2">Performance by Category:</p>
          <div className="space-y-2">
            {categories.map((cat) => (
              <div key={cat.category}>
                <div className="flex items-center justify-between text-sm mb-1">
                  <span className="text-gray-600">{cat.category}</span>
                  <span className={`font-semibold ${getPerformanceColor(cat.percentile)}`}>
                    {getPerformanceIcon(cat.percentile)} {cat.correct}/{cat.total}
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className={`h-full rounded-full transition-all ${
                      cat.percentile >= 75 ? 'bg-green-500' :
                      cat.percentile >= 50 ? 'bg-yellow-500' :
                      'bg-red-500'
                    }`}
                    style={{ width: `${(cat.correct / cat.total) * 100}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Best Category */}
        <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-4">
          <p className="text-sm text-green-800">
            🌟 <strong>Best category:</strong> {bestCategory}
          </p>
        </div>

        {/* Recommendation */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
          <div className="flex items-start gap-2">
            <Lightbulb className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-blue-900 mb-1">Recommendation:</p>
              <p className="text-sm text-blue-700">{recommendation}</p>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-2">
          <Button
            onClick={onPractice}
            className="flex-1 bg-[#5B6FED] hover:bg-[#4A5FDD] rounded-full"
          >
            <Target className="w-4 h-4 mr-2" />
            Practice Mode
          </Button>
          <Button
            onClick={onStudyTips}
            variant="outline"
            className="flex-1 rounded-full border-[#5B6FED] text-[#5B6FED]"
          >
            Study Tips
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

